import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, Modal, InputGroup, Spinner } from "react-bootstrap";
import ObjectFieldSelector from "../fieldselector/ObjectFieldSelector";
import EmailApi from "../../api/EmailApi";

export default function WorkflowSendEmail({
  config,
  onChange,
  objectName,
  readOnly,
}) {
  const [emailInput, setEmailInput] = useState("");
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const systemFields = [
    { label: "Created By Email", value: "{{CreatedBy.Email}}" },
    { label: "Owner Email", value: "{{Owner.Email}}" },
    { label: "Last Modified By Email", value: "{{LastModifiedBy.Email}}" },
  ];

  const [showFieldSelector, setShowFieldSelector] = useState(false);

  const handleFieldSelect = (field) => {
    onChange("configuredObjectEmailField", `{{${field.field_name}}}`);
    //onChange("newValue", `{{${field.field_name}}}`);
    setShowFieldSelector(false);
  };

  useEffect(() => {
    if (config.toEmails) {
      setEmailInput(config.toEmails.join(", "));
    }
  }, [config.toEmails]);

  // Fetch email templates when objectName is available
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!objectName) {
        setTemplates([]);
        return;
      }

      setLoadingTemplates(true);
      try {
        const emailApi = new EmailApi();
        const response = await emailApi.getEmailTemplates(objectName);
        if (response.success) {
          setTemplates(response.data || []);
        } else {
          console.error("Failed to fetch templates:", response.message);
          setTemplates([]);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [objectName]);

  // Populate template content for preview when template is selected (do not clear when no template - that would wipe saved custom subject/body on load)
  useEffect(() => {
    if (templates.length > 0 && config.templateId) {
      const selectedTemplate = templates.find(
        t => t.id === config.templateId || t.id === parseInt(config.templateId)
      );
      if (selectedTemplate) {
        // Populate template content for preview (read-only display)
        if (selectedTemplate.subject) {
          onChange("subject", selectedTemplate.subject);
        }
        if (selectedTemplate.body) {
          onChange("body", selectedTemplate.body);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, config.templateId]);

  const handleTemplateChange = (templateId) => {
    if (!templateId) {
      // Clear template selection
      onChange("templateId", null);
      // Clear subject and body when template is deselected
      onChange("subject", "");
      onChange("body", "");
      return;
    }

    // Store templateId - WorkflowTriggerService will fetch the template
    onChange("templateId", templateId);

    // Find and populate template content for preview
    const selectedTemplate = templates.find(t => t.id === templateId || t.id === parseInt(templateId));
    if (selectedTemplate) {
      // Populate subject and body for preview (read-only)
      onChange("subject", selectedTemplate.subject || "");
      onChange("body", selectedTemplate.body || "");
    }
  };

  // Get the selected template for display
  const selectedTemplate = config.templateId
    ? templates.find(t => t.id === config.templateId || t.id === parseInt(config.templateId))
    : null;

  const validateEmails = (emails) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.filter((email) => !emailRegex.test(email));
  };

  const handleEmailBlur = () => {
    const emails = emailInput
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);

    const invalids = validateEmails(emails);
    setInvalidEmails(invalids);

    // Save all emails including invalid ones
    onChange("toEmails", emails);
  };

  const handleEmailChange = (value) => {
    setEmailInput(value);
    setInvalidEmails([]); // Clear error while typing
  };

  return (
    <>
      <Form>
        <Row>
          <Col lg={6}>
            <Form.Group>
              <Form.Label>Static Email(s)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter comma separated emails"
                value={emailInput}
                onChange={(e) => handleEmailChange(e.target.value)}
                onBlur={handleEmailBlur}
                isInvalid={invalidEmails.length > 0}
                disabled={readOnly}
              />
              {invalidEmails.length > 0 && (
                <Form.Control.Feedback type="invalid">
                  Invalid email(s): {invalidEmails.join(", ")}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Configured Object Email Field</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="e.g. Email"
                  value={config.configuredObjectEmailField || ""}
                  onChange={(e) =>
                    onChange("configuredObjectEmailField", e.target.value)
                  }
                  disabled={readOnly}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowFieldSelector(true)}
                  disabled={readOnly}
                >
                  Select Field
                </Button>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group>
              <Form.Label>-Dynamic System Fields-</Form.Label>
              {systemFields.map((field) => (
                <Form.Check
                  key={field.value}
                  type="checkbox"
                  label={field.label}
                  checked={
                    config.dynamicRecipients?.includes(field.value) || false
                  }
                  onChange={(e) => {
                    const updated = new Set(config.dynamicRecipients || []);
                    if (e.target.checked) {
                      updated.add(field.value);
                    } else {
                      updated.delete(field.value);
                    }
                    onChange("dynamicRecipients", Array.from(updated));
                  }}
                  disabled={readOnly}
                />
              ))}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col lg={12}>
            <Form.Group>
              <Form.Label>Email Template (Optional)</Form.Label>
              {loadingTemplates ? (
                <div className="text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading templates...
                </div>
              ) : (
                <Form.Select
                  value={config.templateId || ""}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">No template (use custom subject/body)</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Text className="text-muted">
                {templates.length > 0
                  ? `Select a template to use. The template will be fetched and used when the workflow runs. ${templates.length} template(s) available.`
                  : objectName
                    ? "No templates available for this object. You can create templates in the setup section."
                    : "Select an object to see available templates."}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col lg={12}>
            <Form.Group>
              <Form.Label>
                Subject {!config.templateId && <span className="text-danger">*</span>}
                {selectedTemplate && (
                  <span className="text-muted ms-2">(Preview from template: {selectedTemplate.name})</span>
                )}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={selectedTemplate ? "Template subject preview" : "Enter email subject"}
                value={config.subject || ""}
                onChange={(e) => onChange("subject", e.target.value)}
                required={!readOnly && !config.templateId}
                disabled={readOnly || !!config.templateId}
                readOnly={!!config.templateId}
                style={config.templateId ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
              />
              {config.templateId && selectedTemplate && (
                <Form.Text className="text-muted">
                  This is a preview of the subject from template "{selectedTemplate.name}". The template will be fetched and used when the workflow runs. Placeholders will be resolved automatically.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col lg={12}>
            <Form.Group>
              <Form.Label>
                Body {!config.templateId && <span className="text-danger">*</span>}
                {selectedTemplate && (
                  <span className="text-muted ms-2">(Preview from template: {selectedTemplate.name})</span>
                )}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                placeholder={selectedTemplate ? "Template body preview" : "Enter email body"}
                value={config.body || ""}
                onChange={(e) => onChange("body", e.target.value)}
                required={!readOnly && !config.templateId}
                disabled={readOnly || !!config.templateId}
                readOnly={!!config.templateId}
                style={config.templateId ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
              />
              {config.templateId && selectedTemplate && (
                <Form.Text className="text-muted">
                  This is a preview of the body from template "{selectedTemplate.name}". The template will be fetched and used when the workflow runs. Placeholders like {"{{FieldName}}"} will be resolved with actual values automatically.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>
      </Form>
      <Modal
        show={showFieldSelector}
        onHide={() => setShowFieldSelector(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ObjectFieldSelector
            objectName={objectName}
            multiSelect={false}
            onSelect={handleFieldSelect}
            fieldTypeFilter="email"
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
